import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Popover } from "antd";
import "./notifications.scss";

import { Badge, Icon, Text, Button, IconFeatured } from "@ui-kit";
import { userCancelNotificationTC, userCheckAuthTC } from "@entities/user/model/user.actions";
import {
  ADMIN_ARCHIVE_PROJECTS_URL,
  ADMIN_USER_PROJECTS_URL,
  ADMIN_PROJECTS_URL,
} from "@app/routes";
const Bell = "/images/icons/bell-gray500.svg";
const Email = "/images/icons/mail-accent600.svg";
const CrownWarningSmal = "/images/icons/crown-warning500.svg";
const Clock = "/images/icons/clock-gray500.svg";
const Dollar = "/images/icons/dollar-gray500.svg";
const CheckFlex = "/images/icons/emoji/check-mark.svg";
const Petard = "/images/icons/emoji/party-popper.svg";
const WavingHand = "/images/icons/emoji/waving-hand.svg";
const Ok = "/images/icons/emoji/okay.svg";
const Selebration = "/images/icons/emoji/partying-face.svg";
const Crown = "/images/icons/emoji/crown.svg";
const ChevronIcon = "/images/icons/chevron-gray500.svg";

const Notifications = ({ children, user = {}, isUser }) => {
  const [open, setOpen] = useState(false);

  const handleOpenBell = (newOpen) => {
    setOpen(newOpen);
  };

  const renderContent = () => {
    if (!user?.notifications?.length) {
      return (
        <>
          <div className="notification__wrapper empty">
            <IconFeatured src={Email} size="sm"/>
            <Text as="h6" className="subtitle text">
              You have no notifications
            </Text>
          </div>
        </>
      )
    }

    return (
      <>
        {user?.notifications?.map((notification, index) => {
          return (
            <div key={index} className="notification__wrapper">
              <Notification notification={notification} isUser={isUser} setOpen={setOpen} />
            </div>
          );
        })}
      </>
    );
  };

  return (
    <div className="d-flex header__link-notification">
      <Popover
        content={renderContent()}
        trigger="click"
        arrow={false}
        placement="bottom"
        overlayClassName="notification__popover"
        open={open}
        onOpenChange={handleOpenBell}
      >
        <>
          <Button
            iconLeft={Bell}
            alt="notifications"
            size="sm"
            theme="link-gray"
            className="notification__bell"
            isDisabled={!user.notificationsCounter}
          >
            {children}
          </Button>
        </>
      </Popover>
    </div>
  );
};

export default Notifications;

const Notification = ({ notification, isUser, setOpen }) => {
  const dispatch = useDispatch();
  const [isViewNotification, setViewNotification] = useState(false);
  const showChevron = isViewNotification ? "up" : "down";
  const showButtonTitle = isViewNotification ? "Show less" : "Learn more";

  const deleteMessage = (id) => {
    dispatch(userCancelNotificationTC(id));
    dispatch(userCheckAuthTC());
    setOpen(false)
  };

  const toNavigate = (id, url) => {
    window.location.href = url;
    deleteMessage(id);
  };

  const viewNotification = () => {
    setViewNotification(!isViewNotification);
  };

  const getUrl = (projectId, isArchive = 0) => {
    if (isUser) {
      return (
        ADMIN_USER_PROJECTS_URL.pathname + `?project_asset_id=${projectId}`
      );
    }
    if (+isArchive) {
      return (
        ADMIN_ARCHIVE_PROJECTS_URL.pathname +
        `?project_asset_id=${projectId}&search=${projectId}`
      );
    }

    return ADMIN_PROJECTS_URL.pathname + `?&project_asset_id=${projectId}`;
  };

  const { 
    id = '', 
    data = {}, 
    resourceId = '', 
    resource = {} 
  } = notification;
  const { 
    markType = '', 
    hours = '', 
    minutes = '', 
    notificationValue = '', 
    rate = '',
    text = ''
  } = data;

  const notification_url = getUrl(resourceId, resource?.isArchived);
  const licensesBage =
    rate && rate === "hourlyWithoutLicense" ? (
      <Badge icon={CrownWarningSmal} theme="warning" text="Exclusive license" />
    ) : (
      <Badge
        status={true}
        theme="blue-gray"
        text="Extended commercial license"
      />
    );

  const messageText = () => {
    if (!text) return '';

    return text.replaceAll('<br>', ' ');
  }

  const renderTime = () => {
    return (
      <div className="time">
        <Icon src={Clock} alt="clock" />
        <Text as="span">{hours ? hours : "00"}</Text>
        <Text as="span">{minutes ? `:${minutes}` : ":00"}</Text>
      </div>
    );
  };

  const renderPrice = () => {
    return (
      <div className="cost">
        <Icon src={Dollar} alt="usd" />
        <Text as="span">{notificationValue}</Text>
      </div>
    );
  };

  const renderActions = () => {
    if (isUser) {
      const paymentsType = ["sketchReady", "illustrationReady", "sourceReady"];
      const paymentTypeFound = paymentsType.find((type) => type === markType);
      const hasTimeSpent = !!(hours + minutes);
      const isViewCheckout = paymentTypeFound && hasTimeSpent;

      return (
        <div className="d-flex actions">
          {!isViewCheckout && (
            <Button
              theme="link-gray"
              size="md"
              onClick={() => deleteMessage(id)}
            >
              Cancel
            </Button>
          )}
          <Button
            onClick={() => toNavigate(id, notification_url)}
            theme="link-color"
            size="md"
            iconRight={Email}
          >
            View project
          </Button>
          {isViewCheckout && (
            <Button
              onClick={() => toNavigate(id, `/checkout/projects/${resourceId}`)}
              theme="link-color"
              className="notification__link"
            >
              Go to the payment
            </Button>
          )}
        </div>
      );
    }

    return (
      <div className="d-flex actions">
        <Button theme="link-gray" size="md" onClick={() => deleteMessage(id)}>
          Cancel
        </Button>
        <Button
          onClick={() => toNavigate(id, notification_url)}
          theme="link-color"
          size="md"
          iconRight={Email}
        >
          View project
        </Button>
      </div>
    );
  };

  const renderNotification = () => {
    // CHAT MESSAGE
    if (notification.type === "PROJECT_MESSAGE_TYPE") {
      return (
          <div className="notification__message">
            <Text as="h6" className="title">
              You have new messages
            </Text>
            <Text as="h6" className="subtitle text">
              {messageText()}
            </Text>
            <div className="d-flex actions">
              <Button
                theme="link-gray"
                size="md"
                onClick={() => deleteMessage(id)}
              >
                Cancel
              </Button>
              <Button
                onClick={() => toNavigate(id, notification_url)}
                theme="link-color"
                size="md"
                iconRight={Email}
              >
                View chat
              </Button>
            </div>
          </div>
      );
    }

    // PROJECT STATUS
    if (notification.type === "PROJECT_NOTIFICATION_TYPE") {
      switch (markType) {
        case "sketchReady":
          return (
              <div className="notification__message">
                <div className="d-flex ai-center mb-9 descr jc-sb pr">
                  <Text as="h6" className="title">
                    The sketch is ready
                    <Icon
                      src={CheckFlex}
                      alt="notification-check"
                      className="title__icon"
                    />
                  </Text>
                </div>

                <Text as="h5" className={`subtitle ${showChevron}`}>
                  The sketch for your illustration is ready. You can suggest
                  edits, if necessary. Please make a payment, and we will continue
                  the project.
                </Text>
                <div className={`info ${showChevron}`}>
                  {licensesBage}
                  {renderTime()}
                  {renderPrice()}
                </div>
                {renderActions()}
                <div className="show-less">
                  <Button
                    size="sm"
                    theme="link-gray"
                    iconRight={ChevronIcon}
                    onClick={viewNotification}
                    className={showChevron}
                  >
                    {showButtonTitle}
                  </Button>
                </div>
              </div>
          );

        case "illustrationReady":
          return (
              <div className="notification__message">
                <div className="d-flex ai-center mb-9 descr jc-sb pr">
                  <Text as="h6" className="title">
                    Your illustration is ready
                    <Icon
                      src={Petard}
                      alt="notification-check"
                      className="title__icon"
                    />
                  </Text>
                </div>
                <Text as="h5" className={`subtitle ${showChevron}`}>
                  Your illustration is ready. You can suggest edits, if necessary.
                  Please make a payment, and we will continue the project.
                </Text>
                <div className={`info ${showChevron}`}>
                  {licensesBage}
                  {renderTime()}
                  {renderPrice()}
                </div>
                {renderActions()}
                <div className="show-less">
                  <Button
                    size="sm"
                    theme="link-gray"
                    iconRight={ChevronIcon}
                    onClick={viewNotification}
                    className={showChevron}
                  >
                    {showButtonTitle}
                  </Button>
                </div>
              </div>
          );

        case "sourceReady":
          return (
              <div className="notification__message">
                <div className="d-flex ai-center mb-9 descr jc-sb pr">
                  <Text as="h6" className="title">
                    Just one more step to download the files
                    <Icon
                      src={Selebration}
                      alt="notification-selebration"
                      className="title__icon"
                    />
                  </Text>
                </div>
                <Text as="h5" className={`subtitle ${showChevron}`}>
                  The illustration is ready and it has been added to the set. It
                  will be available for download after payment.
                </Text>
                <div className={`info ${showChevron}`}>
                  {licensesBage}
                  {renderTime()}
                  {renderPrice()}
                </div>
                {renderActions()}
                <div className="show-less">
                  <Button
                    size="sm"
                    theme="link-gray"
                    iconRight={ChevronIcon}
                    onClick={viewNotification}
                    className={showChevron}
                  >
                    {showButtonTitle}
                  </Button>
                </div>
              </div>
          );

        case "waitingResponse":
          return (
              <div className="notification__message">
                <div className="d-flex ai-center mb-9 descr jc-sb pr">
                  <Text as="h6" className="title">
                    We are waiting for your answer
                    <Icon
                      src={WavingHand}
                      alt="notification-check"
                      className="title__icon"
                    />
                  </Text>
                </div>
                <Text as="h5" className={`subtitle ${showChevron}`}>
                  You have received a question from the Interviewer. Please answer
                  in the chat below so that we can complete the brief and continue
                  the work on your illustration.
                </Text>
                {renderActions()}
                <div className="show-less">
                  <Button
                    size="sm"
                    theme="link-gray"
                    iconRight={ChevronIcon}
                    onClick={viewNotification}
                    className={showChevron}
                  >
                    {showButtonTitle}
                  </Button>
                </div>
              </div>
          );

        case "subscriptionRequired":
          return (
              <div className="notification__message">
                <div className="d-flex ai-center mb-9 descr jc-sb pr">
                  <Text as="h6" className="title">
                    Subscription payment required
                    <Icon
                      src={Dollar}
                      alt="notification-dollar"
                      className="title__icon"
                    />
                  </Text>
                </div>
                <Text as="h5" className={`subtitle ${showChevron}`}>
                  To continue working on your project, you need to choose and
                  pay for a subscription plan.
                </Text>
                <div className="d-flex actions">
                  <Button
                    theme="link-gray"
                    size="md"
                    onClick={() => deleteMessage(id)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      setOpen(false);
                      window.location.href = "/#prices";
                    }}
                    theme="link-color"
                    size="md"
                  >
                    View plans
                  </Button>
                </div>
                <div className="show-less">
                  <Button
                    size="sm"
                    theme="link-gray"
                    iconRight={ChevronIcon}
                    onClick={viewNotification}
                    className={showChevron}
                  >
                    {showButtonTitle}
                  </Button>
                </div>
              </div>
          );

        //TODO
        case "switchingToExclusive":
          return (
              <div className="notification__message">
                <div className="d-flex ai-center mb-9 descr jc-sb pr">
                  <Text as="h6" className="title">
                    Switching to an exclusive license
                    <Icon
                      src={Crown}
                      alt="notification-check"
                      className="title__icon"
                    />
                  </Text>
                </div>
                <Text as="h5" className={`subtitle ${showChevron}`}>
                  When the rate changes, the cost of work switches from{" "}
                  <span>$23/hour</span>
                  to <span>$80/hour.</span> Please confirm the rate change.
                </Text>
                {isUserRole && (
                  <div className="actions">
                    <Button theme="secondary-gray">Decline</Button>
                    <Button>Confirm</Button>
                  </div>
                )}
                <div className="show-less">
                  <Button
                    size="sm"
                    theme="link-gray"
                    iconRight={ChevronIcon}
                    onClick={viewNotification}
                    className={showChevron}
                  >
                    {showButtonTitle}
                  </Button>
                </div>
              </div>
          );

        case "workProgress":
          return (
              <div className="notification__message">
                <div className="d-flex ai-center mb-9 descr jc-sb pr">
                  <Text as="h6" className="title">
                    We've passed all your wishes to the artis
                    <Icon
                      src={Ok}
                      alt="notification-check"
                      className="title__icon"
                    />
                  </Text>
                </div>
                <Text as="h5" className={`subtitle ${showChevron}`}>
                  When the work is ready, you'll see it in the chat.
                </Text>

                {renderActions()}
                <div className="show-less">
                  <Button
                    size="sm"
                    theme="link-gray"
                    iconRight={ChevronIcon}
                    onClick={viewNotification}
                    className={showChevron}
                  >
                    {showButtonTitle}
                  </Button>
                </div>
              </div>
          );

        default: {}
      }
    }
  };

  return (<>{renderNotification()}</>)
};
